import * as React from 'react';
import { Field, formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import { flow, forEachRight, get, head, map, has } from 'lodash';
import { IBrandingState, II18nMessages } from '../../../../../../services/branding/models';
import { Button, Col, ControlLabel, FormGroup, Row, Alert, Popover, OverlayTrigger } from 'react-bootstrap';
import FieldFormControl from '../../../../../../components/FieldFormControl/index';
import SelectControl from '../../../../../../components/SelectControl/index';
import FaTrash from 'react-icons/fa/trash';
import * as uuid from 'uuid/v4';
import * as moment from 'moment';
import {
  isAdult,
  isAfter,
  isEmail,
  isPhone,
  isPostcode,
  isRequired,
  isSameEmail,
  isValidDate,
  isOlderThen,
} from '../../../../../../util/validators';
import { IPostcodeAddress, IProduct } from '../../../../../../data/api/models';
import { IStore } from '../../../../../../redux/IStore';
import PostcodeControl from '../../../../../../components/PostcodeControl';
import FaInfoCircle from 'react-icons/fa/info-circle';
import * as Commonmark from 'react-commonmark';

type TravellerType = 'child' | 'adult';

interface ITravellerTypeConfig {
  min: number;
  max: number;
}

interface IGroupType {
  adult: ITravellerTypeConfig;
  child?: ITravellerTypeConfig;
}

interface IGroupTypeMap {
  [type: string]: IGroupType;
}

interface ITripDetailsForm {
  i18n: II18nMessages;
  pristine: boolean;
  fields: any;
  groupType: string;
  travellers: any;
  tripType: string;
  renderDefinition(name: string): any;
  product: IProduct;
  branding: IBrandingState;
}

const groupTypeTravellerMap: IGroupTypeMap = {
  individual: {
    adult: {
      min: 1,
      max: 10,
    },
    child: {
      min: 0,
      max: 10,
    },
  },
  holiday_booking: {
    adult: {
      min: 1,
      max: 10,
    },
    child: {
      min: 0,
      max: 10,
    },
  },
  couple: {
    adult: {
      min: 2,
      max: 2,
    },
    child: {
      min: 0,
      max: 0,
    },
  },
  single_parent_family: {
    adult: {
      min: 1,
      max: 1,
    },
    child: {
      min: 1,
      max: 10,
    },
  },
  family: {
    adult: {
      min: 2,
      max: 2,
    },
    child: {
      min: 1,
      max: 10,
    },
  },
  independent_child: {
    adult: {
      min: 0,
      max: 0,
    },
    child: {
      min: 1,
      max: 10,
    },
  },
};

const defaultTraveller = {
  id: '',
  title: '',
  firstName: '',
  lastName: '',
  dob: '',
  phone: '',
  email: '',
  type: '',
  screening: null,
  screeningSessionId: null,
};

const childTitles = [
  { label: 'Master', value: 'Master' },
  { label: 'Mx', value: 'Mx' },
  { label: 'Miss', value: 'Miss' },
  { label: 'Mr', value: 'Mr' },
];

const adultTitles = [
  { label: 'Mr', value: 'Mr' },
  { label: 'Mrs', value: 'Mrs' },
  { label: 'Miss', value: 'Miss' },
  { label: 'Ms', value: 'Ms' },
  { label: 'Master', value: 'Master' },
  { label: 'Mx', value: 'Mx' },
  { label: 'Dr', value: 'Dr' },
  { label: 'Prof', value: 'Prof' },
  { label: 'Rev', value: 'Rev' },
  { label: 'Cpt', value: 'Cpt' },
  { label: 'Sir', value: 'Sir' } ,
  { label: 'Dame', value: 'Dame' },
  { label: 'Lord', value: 'Lord' },
  { label: 'Lady', value: 'Lady' },
  { label: 'Rt Honourable', value: 'Rt Honourable' },
];

class Travellers extends React.Component<ITripDetailsForm> {
  private getAll = (type: TravellerType, fields) => fields.getAll().filter((traveller) => traveller.type === type);
  private count = (type: TravellerType, fields) => this.getAll(type, fields).length;
  private handleAddField = (type: TravellerType, fields) => () => this.addField(type, fields);
  private handleRemoveField = (id, fields) => () => this.removeField(id, fields);

  public state = {
    isEmailEqual: false,
  };

  private handleTravellerUpdates = (type: TravellerType, config: ITravellerTypeConfig, fields) => {
    let total = this.count(type, fields);

    if (total > config.max) {
      // Remove travellers from the form until the maximum amount of travellers are
      // achieved for the next group type.

      forEachRight(this.getAll(type, fields), (traveller) => {
        if (total <= config.max) {
          return;
        }

        this.removeField(traveller.id, fields);
        total -= 1;
      });
    }

    if (total < config.min) {
      const totalToAdd = config.min - total;

      for (let i = 0; i < totalToAdd; i++) {
        this.addField(type, fields);
      }
    }
  }

  public componentWillReceiveProps(nextProps: ITripDetailsForm) {
    if (this.props.groupType === nextProps.groupType) {
      return;
    }

    const { fields, groupType } = nextProps;
    const config = groupTypeTravellerMap[ groupType ? groupType : 'individual' ];

    if (config) {
      const { adult, child } = config;

      this.handleTravellerUpdates('adult', adult, fields);
      this.handleTravellerUpdates('child', child, fields);
    }

    // check if the fields are equal
  }

  private addField = (type: TravellerType, fields) => {
    fields.push({
      ...defaultTraveller,
      id: uuid(),
      type,
    });
  }

  private getIndex = (id: string, fields) => {
    let travellerIndex = -1;
    const travellers = fields.getAll();

    travellers.forEach((traveller, index) => {
      if (traveller.id === id) {
        travellerIndex = index;
      }
    });

    if (travellerIndex === -1) {
      console.error('Couldn\'t find traveller', travellers, id, travellerIndex);
    }

    return travellerIndex;
  }

  private removeField = (id: string, fields) => {
    const travellerIndex = this.getIndex(id, fields);

    if (travellerIndex === -1) {
      return;
    }

    fields.remove(travellerIndex);
  }

  private handleEmailOnChange = () => {
    this.setState(() => ({
      isEmailEqual: false,
    }));
  }

  private handleOnBlue = () => {
    const { travellers } = this.props;

    const getEmail = get(head(travellers), 'email');
    const getConfirmEmail = get(head(travellers), 'confirmEmail');

    if (getEmail === getConfirmEmail) {
      this.setState(() => ({
        isEmailEqual: true,
      }));
    } else {
      this.setState(() => ({
        isEmailEqual: false,
      }));
    }
  }

  private handleSelectAddress = (address: IPostcodeAddress) => {
    const {change} = this.props;
    const base = 'travellers[0].address';

    change(`${base}.line1`, address.line1);
    change(`${base}.line2`, address.line2);
    change(`${base}.town`, address.town);
    change(`${base}.county`, address.county);
    change(`${base}.postcode`, address.postcode);
  }

  private renderLeadTravellerFields = (traveller, fieldIndex) => {
    const { i18n, product, branding, travellers } = this.props;

    const residencies = map(product.metadata.residencies, (item, key) => ({
      label: item,
      value: key,
    }));

    const infoIconResidency = (
      <Popover id="popover-trigger-click-root-close" title="Residency">
        <Commonmark
          source={'United Kingdom (England, Scotland, Wales and Northern Ireland) ' +
          'and The Isle of Man.'}
        />
      </Popover>
    );

    return (
      <div>
        <Col sm={12}>
          <FormGroup controlId="phone">
            <Col componentClass={ControlLabel} sm={3}>
              {i18n.quote.tripDetails.traveller.phone}:
            </Col>
            <Col sm={6}>
              <Field
                name={`travellers[${fieldIndex}].phone`}
                type="number"
                component={FieldFormControl}
                validate={[isRequired, isPhone]}
              />
            </Col>
          </FormGroup>
        </Col>
        <Col sm={12}>
          <FormGroup controlId="email">
            <Col componentClass={ControlLabel} sm={3}>
              {i18n.quote.tripDetails.traveller.email}:
            </Col>
            <Col sm={6}>
              <Field
                onChange={this.handleEmailOnChange}
                name={`travellers[${fieldIndex}].email`}
                type="email"
                component={FieldFormControl}
                validate={[isRequired, isEmail]}
              />
            </Col>
          </FormGroup>
        </Col>
        { !this.state.isEmailEqual && (
          <Col sm={12}>
            <FormGroup controlId="email">
              <Col componentClass={ControlLabel} sm={3}>
                {i18n.quote.tripDetails.traveller.confirmEmail}:
              </Col>
              <Col sm={6}>
                <Field
                  onBlur={this.handleOnBlue}
                  name={`travellers[${fieldIndex}].confirmEmail`}
                  type="email"
                  component={FieldFormControl}
                  validate={[isRequired, isEmail, isSameEmail(travellers[0].email)]}
                />
              </Col>
            </FormGroup>
          </Col>
        )}
        {branding.channel.channelType !== 'AGG' && (
          <Col sm={12}>
            <FormGroup controlId="postcode">
              <Col componentClass={ControlLabel} sm={3}>
                Postcode:
              </Col>
              <Col sm={4}>
                <Field
                  name={`travellers[${fieldIndex}].address.postcode`}
                  component={FieldFormControl}
                  validate={[isRequired, isPostcode]}
                />
              </Col>
            </FormGroup>
          </Col>
        )}
        {branding.channel.channelType === 'AGG' && (
          <Col sm={12}>
            <FormGroup controlId="postcode">
              <Col componentClass={ControlLabel} sm={3}>
                Postcode:
              </Col>
              <Col sm={6}>
                <Field
                  name="travellers[0].address.postcode"
                  type="text"
                  component={PostcodeControl}
                  onSelectAddress={this.handleSelectAddress}
                  validate={[
                    isRequired,
                    isPostcode,
                  ]}
                />
              </Col>
            </FormGroup>
            <FormGroup controlId="address">
              <Col componentClass={ControlLabel} sm={3}>
                {i18n.quote.tripDetails.traveller.address}
              </Col>
              <Col sm={6}>
                <Field
                  className="line-top"
                  name="travellers[0].address.line1"
                  placeholder="Line 1"
                  type="text"
                  component={FieldFormControl}
                  validate={[
                    isRequired,
                  ]}
                />
                <Field
                  className="line-middle"
                  name="travellers[0].address.line2"
                  placeholder="Line 2"
                  type="text"
                  component={FieldFormControl}
                />
                <Field
                  className="line-middle"
                  name="travellers[0].address.town"
                  type="text"
                  placeholder="Town"
                  component={FieldFormControl}
                />
                <Field
                  className="line-bottom"
                  name="travellers[0].address.county"
                  placeholder="County"
                  type="text"
                  component={FieldFormControl}
                />
              </Col>
            </FormGroup>
          </Col>
        )}
      </div>
    );
  }

  private renderFields(type: TravellerType) {
    const { fields, i18n, groupType, branding } = this.props;
    const hasGroupTypeKey = has(groupTypeTravellerMap, groupType);
    const fallback = 'family';
    const config = hasGroupTypeKey
      ? groupTypeTravellerMap[ groupType ? groupType : 'individual' ]
      : groupTypeTravellerMap[fallback];

    const travellerConfig = config[type];
    const channelKey = branding.channel.key;

    return this.getAll(type, fields).map((traveller, index) => {
      const fieldIndex = this.getIndex(traveller.id, fields);
      const isTravellerTypeAdult = type === 'adult';
      const doNothing = () => {/*noop*/};
      const isGroupTypeFamily = groupType === 'family' || groupType === 'single_parent_family';
      const adultDobValidation = isOlderThen(18);
      const childDobValidation = isOlderThen(0);
      const travellerType = traveller.type === 'child' ? 'children' : traveller.type;
      const maxAgePath = isGroupTypeFamily ? 'products[0].metadata.age_limit.family.' + travellerType + '.max_age' :
        'products[0].metadata.age_limit.' + travellerType + '.max_age';
      let productMaxAge = get(branding.channel, maxAgePath, 79);
      const dobValidator = isTravellerTypeAdult ? adultDobValidation : childDobValidation;
      if (channelKey === 'tumc-b2c') {
        productMaxAge = 110;
      }

      return (
        <Row className="t-container" key={index}>
          <Col sm={12}>
            <Col sm={12} className="traveller margin-top">
              {index === 0 && type === 'adult' && (<h4>{i18n.quote.tripDetails.traveller.lead}</h4>)}
              {(index > 0 || type !== 'adult') && (<h4>{i18n.quote.tripDetails.traveller[type]} {index + 1}</h4>)}
              <div className="trash-btn">
                <Button
                  bsStyle="primary"
                  onClick={this.handleRemoveField(traveller.id, fields)}
                  className={index <= (travellerConfig.min - 1) ? 'hidden' : ''}
                >
                  <FaTrash />
                </Button>
              </div>
              <Row>
                <Col sm={12}>
                  <FormGroup controlId="name">
                    <Col componentClass={ControlLabel} sm={3}>
                      {i18n.quote.tripDetails.traveller.name}:
                    </Col>
                    <Col sm={2}>
                      <Field
                        name={`travellers[${fieldIndex}].title`}
                        id={`travellers[${fieldIndex}].title`}
                        type="text"
                        placeholder="Title"
                        options={type === 'adult' ? adultTitles : childTitles}
                        component={SelectControl}
                        clearable={false}
                        validate={[isRequired]}
                      />
                    </Col>
                    <Col sm={4}>
                      <Field
                        name={`travellers[${fieldIndex}].firstName`}
                        type="text"
                        placeholder="First Name"
                        component={FieldFormControl}
                        validate={[isRequired]}
                      />
                    </Col>
                    <Col sm={3}>
                      <Field
                        name={`travellers[${fieldIndex}].lastName`}
                        type="text"
                        placeholder="Last Name"
                        component={FieldFormControl}
                        validate={[isRequired]}
                      />
                    </Col>
                  </FormGroup>
                </Col>
                <Col sm={12}>
                  <FormGroup controlId="dob">
                    <Col componentClass={ControlLabel} sm={3}>
                      {i18n.quote.tripDetails.traveller.dob}:
                    </Col>
                    <Col sm={4}>
                      <Field
                        name={`travellers[${fieldIndex}].dob`}
                        type="masked-date"
                        readOnly={branding.channel.channelType === 'AGG'}
                        component={FieldFormControl}
                        placeholder="dd/mm/yyyy"
                        className="form-control"
                        validate={[
                          isRequired,
                          isValidDate,
                          dobValidator,
                          isAfter(moment().startOf('day').
                          subtract(productMaxAge, 'years'), 'Sorry we cannot provide a quote due to your age.'),
                        ]}
                      />
                    </Col>
                  </FormGroup>
                </Col>
                {index === 0 && type === 'adult' && this.renderLeadTravellerFields(traveller, fieldIndex)}
                {index === 0 && type === 'child' && groupType === 'independent_child' &&
                this.renderLeadTravellerFields(traveller, fieldIndex)}
              </Row>
            </Col>
          </Col>
        </Row>
      );
    });
  }

  public componentWillMount() {
    const { fields } = this.props;
    const fieldLength = fields.length;
    this.handleOnBlue();

    if (fieldLength === 0) {
      this.addField('adult', fields);
    }
  }

  public render() {
    const { i18n, fields, groupType, branding } = this.props;
    const hasGroupTypeKey = has(groupTypeTravellerMap, groupType);
    const fallback = 'family';
    const config = hasGroupTypeKey
      ? groupTypeTravellerMap[ groupType ? groupType : 'individual' ]
      : groupTypeTravellerMap[fallback];

    return (
      <div className="section">
        <Row>
          {!hasGroupTypeKey && (
            <Alert bsStyle="danger">
              Invalid group type.
            </Alert>
          )}
          <div className="traveller-section">
            {this.renderFields('adult')}
          </div>
          {branding.channel.channelType !== 'AGG' && branding.channel.key !== 'cpi-b2c' && (
            <Button
              bsStyle="secondary"
              id="add-adult"
              onClick={this.handleAddField('adult', fields)}
              className={this.count('adult', fields) >= config.adult.max ? 'hidden' : ''}
            >
              {i18n.quote.button.adult}

            </Button>
          )}

          <div className={config.child.max === 0 ? 'hidden' : 'margin-top'}>
            <div className="traveller-section">
              {this.renderFields('child')}
            </div>
            {branding.channel.channelType !== 'AGG' && branding.channel.key !== 'cpi-b2c' && (
              <Button
                bsStyle="secondary"
                id="add-child"
                onClick={this.handleAddField('child', fields)}
                className={this.count('child', fields) >= config.child.max ? 'hidden' : ''}
              >
                {i18n.quote.button.child}
              </Button>
            )}
          </div>
        </Row>
      </div>
    );
  }
}

let selector;

export default flow([
  (component) => {
    selector = formValueSelector('quote');

    return component;
  },
  connect(
    (state: IStore) => {
      const groupType = selector(state, 'groupType');
      const tripType = selector(state, 'tripType');
      const travellers = selector(state, 'travellers');

      return {
        groupType,
        tripType,
        travellers,
        branding: state.branding,
      };
    },
  ),
])(Travellers);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/app/scenes/Quote/scenes/TripDetails/components/Travellers/index.tsx